import React, { useState, useRef, useEffect } from 'react';
import { Mail, RefreshCw, CheckCircle2, AlertCircle } from 'lucide-react';
import api from '../services/api';

interface EmailVerificationProps {
    email: string;
    onVerified: () => void;
    onCancel?: () => void;
}

const EmailVerification: React.FC<EmailVerificationProps> = ({ email, onVerified, onCancel }) => {
    const [code, setCode] = useState(['', '', '', '', '', '']);
    const [isVerifying, setIsVerifying] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [resendCountdown, setResendCountdown] = useState(0);

    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    useEffect(() => {
        // Focus first input on mount
        inputRefs.current[0]?.focus();
    }, []);

    useEffect(() => {
        // Countdown timer for resend button
        if (resendCountdown > 0) {
            const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [resendCountdown]);

    const handleChange = (index: number, value: string) => {
        if (value.length > 1) return; // Only allow single digit

        const newCode = [...code];
        newCode[index] = value;
        setCode(newCode);
        setError(null);

        // Auto-focus next input
        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }

        // Auto-verify when all 6 digits entered
        if (index === 5 && value) {
            const fullCode = newCode.join('');
            if (fullCode.length === 6) {
                verifyCode(fullCode);
            }
        }
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
        if (e.key === 'Backspace' && !code[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);

        if (pastedData.length === 6) {
            const newCode = pastedData.split('');
            setCode(newCode);
            inputRefs.current[5]?.focus();
            verifyCode(pastedData);
        }
    };

    const verifyCode = async (fullCode: string) => {
        setIsVerifying(true);
        setError(null);

        try {
            await api.verifyEmail(email, fullCode);

            setSuccess(true);
            setTimeout(() => {
                onVerified();
            }, 1500);
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Invalid verification code');
            setCode(['', '', '', '', '', '']);
            inputRefs.current[0]?.focus();
        } finally {
            setIsVerifying(false);
        }
    };

    const handleResend = async () => {
        setIsResending(true);
        setError(null);

        try {
            await api.sendVerificationCode(email);
            setResendCountdown(60); // 60 seconds cooldown
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Failed to resend code');
        } finally {
            setIsResending(false);
        }
    };

    if (success) {
        return (
            <div className="text-center py-12">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-900/30 border border-green-800 mb-4">
                    <CheckCircle2 className="w-8 h-8 text-green-400" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Email Verified!</h2>
                <p className="text-slate-400">Redirecting to your dashboard...</p>
            </div>
        );
    }

    return (
        <div className="w-full max-w-md mx-auto">
            {/* Header */}
            <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 border border-primary/30 mb-4">
                    <Mail className="w-8 h-8 text-primary" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Verify Your Email</h2>
                <p className="text-slate-400">
                    We sent a 6-digit code to <span className="text-white font-medium">{email}</span>
                </p>
            </div>

            {/* Code Input */}
            <div className="mb-6">
                <label className="block text-sm font-medium text-slate-300 mb-3 text-center">
                    Enter verification code
                </label>
                <div className="flex gap-2 justify-center" onPaste={handlePaste}>
                    {code.map((digit, index) => (
                        <input
                            key={index}
                            ref={(el) => (inputRefs.current[index] = el)}
                            type="text"
                            inputMode="numeric"
                            maxLength={1}
                            value={digit}
                            onChange={(e) => handleChange(index, e.target.value.replace(/\D/g, ''))}
                            onKeyDown={(e) => handleKeyDown(index, e)}
                            disabled={isVerifying}
                            className={`w-12 h-14 text-center text-2xl font-bold rounded-xl border-2
                                ${error
                                    ? 'border-red-800 bg-red-900/20 text-red-400'
                                    : 'border-slate-700 bg-slate-800 text-white'
                                }
                                focus:border-primary focus:ring-2 focus:ring-primary/50 focus:outline-none
                                disabled:opacity-50 disabled:cursor-not-allowed
                                transition-all`}
                        />
                    ))}
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="mb-4 p-3 bg-red-900/20 border border-red-800 rounded-lg flex items-center gap-2 text-red-400 text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{error}</span>
                </div>
            )}

            {/* Resend Button */}
            <div className="text-center">
                <p className="text-slate-400 text-sm mb-2">Didn't receive the code?</p>
                <button
                    onClick={handleResend}
                    disabled={isResending || resendCountdown > 0}
                    className="inline-flex items-center gap-2 text-primary hover:text-primary-hover disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
                >
                    <RefreshCw className={`w-4 h-4 ${isResending ? 'animate-spin' : ''}`} />
                    {resendCountdown > 0
                        ? `Resend in ${resendCountdown}s`
                        : isResending
                            ? 'Sending...'
                            : 'Resend Code'
                    }
                </button>
            </div>

            {/* Cancel Button */}
            {onCancel && (
                <div className="text-center mt-6">
                    <button
                        onClick={onCancel}
                        className="text-slate-400 hover:text-white text-sm transition-colors"
                    >
                        Use a different email
                    </button>
                </div>
            )}

            {/* Security Note */}
            <div className="mt-8 p-4 bg-slate-800/50 border border-slate-700 rounded-lg">
                <p className="text-xs text-slate-400 text-center">
                    🔒 This code will expire in 10 minutes. Never share your verification code with anyone.
                </p>
            </div>
        </div>
    );
};

export default EmailVerification;
